import { forwardRef } from 'react';
import FormInput from '../FormInput';
import type { ComponentPropsWithoutRef } from 'react';
import type { Language } from '@/chassis/ui/i18n/i18n-helper';

type I18nLabel = Record<Language, string>;

// ============================================
// Numeric Types - Internal Types do Backend
// ============================================

// Numeric (base - allow all)
interface NumericProps extends Omit<ComponentPropsWithoutRef<'input'>, 'type'> {
    formInputSize?: 'sm' | 'lg';
    rounded?: boolean;
    label?: string;
    i18nLabel?: I18nLabel | string;
    wrapperClassName?: string;
}

export const FormNumeric = forwardRef<HTMLInputElement, NumericProps>(({ formInputSize, rounded, ...props }, ref) => {
    return <FormInput {...props} ref={ref} type="number" step="0.01" formInputSize={formInputSize} rounded={rounded} />;
});
FormNumeric.displayName = 'FormNumeric';

// Numeric.Positive (>= 0, allow float)
interface NumericPositiveProps extends Omit<ComponentPropsWithoutRef<'input'>, 'type' | 'step' | 'min'> {
    formInputSize?: 'sm' | 'lg';
    rounded?: boolean;
    label?: string;
    i18nLabel?: I18nLabel | string;
    wrapperClassName?: string;
}

export const FormNumericPositive = forwardRef<HTMLInputElement, NumericPositiveProps>(({ formInputSize, rounded, ...props }, ref) => {
    return <FormInput {...props} ref={ref} type="number" min={0} step="0.01" formInputSize={formInputSize} rounded={rounded} />;
});
FormNumericPositive.displayName = 'FormNumericPositive';

// Numeric.Integer (integers only)
interface NumericIntegerProps extends Omit<ComponentPropsWithoutRef<'input'>, 'type' | 'step'> {
    formInputSize?: 'sm' | 'lg';
    rounded?: boolean;
    label?: string;
    i18nLabel?: I18nLabel | string;
    wrapperClassName?: string;
}

export const FormNumericInteger = forwardRef<HTMLInputElement, NumericIntegerProps>(({ formInputSize, rounded, ...props }, ref) => {
    return <FormInput {...props} ref={ref} type="number" step="1" formInputSize={formInputSize} rounded={rounded} />;
});
FormNumericInteger.displayName = 'FormNumericInteger';

// Numeric.Integer.Positive (>= 0, integers only)
interface NumericIntegerPositiveProps extends Omit<ComponentPropsWithoutRef<'input'>, 'type'> {
    formInputSize?: 'sm' | 'lg';
    rounded?: boolean;
    label?: string;
    i18nLabel?: I18nLabel | string;
    wrapperClassName?: string;
}

export const FormNumericIntegerPositive = forwardRef<HTMLInputElement, NumericIntegerPositiveProps>(({ formInputSize, rounded, ...props }, ref) => {
    return <FormInput {...props} ref={ref} type="number" min={0} step="1" formInputSize={formInputSize} rounded={rounded} />;
});
FormNumericIntegerPositive.displayName = 'FormNumericIntegerPositive';

// Numeric.Pin (n digits, password)
interface NumericPinProps extends Omit<ComponentPropsWithoutRef<'input'>, 'type' | 'maxLength'> {
    digits: number;
    formInputSize?: 'sm' | 'lg';
    rounded?: boolean;
    label?: string;
    i18nLabel?: I18nLabel | string;
    wrapperClassName?: string;
}

export const FormNumericPin = forwardRef<HTMLInputElement, NumericPinProps>(({ digits, formInputSize, rounded, onChange, ...props }, ref) => {
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        // Only digits
        e.target.value = e.target.value.replace(/\D/g, '').slice(0, digits);
        onChange?.(e);
    };

    return <FormInput {...props} ref={ref} type="password" maxLength={digits} onChange={handleChange} formInputSize={formInputSize} rounded={rounded} placeholder={props.placeholder || '•'.repeat(digits)} />;
});
FormNumericPin.displayName = 'FormNumericPin';

// Aliases removed: consume Form*-prefixed components instead
